import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useSelectedSubscription } from '@/lib/use-selected-subscription';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton, SkeletonCard, SkeletonLine, SkeletonPage } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { IconChip, SectionLabel, StatusPill, type Tone } from '@/components/ui/calm';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { getWebApp, getTgUser, haptic, openInvoiceUrl } from '@/lib/telegram';
import { telegramShareUrl } from '@/lib/clipboard';
import { toast } from 'sonner';
import { APP_VERSION } from '@/lib/version';
import { formatWholeMinor } from '@/lib/currency';
import { formatNumber } from '@/lib/date';
import { WITHDRAW_QUERY_KEY } from '@/lib/withdraw';
import { useOpenSite } from '@/lib/site-link';
import {
  Wallet,
  Users,
  Gift,
  Store,
  Smartphone,
  ShieldCheck,
  Snowflake,
  Settings as SettingsIcon,
  LogOut,
  ChevronRight,
  Copy,
  Trophy,
  Plus,
  History as HistoryIcon,
  MessageSquare,
  HelpCircle,
  Activity,
  Fingerprint,
  ExternalLink,
  Check,
  Laptop,
  Loader2,
  type LucideIcon,
} from 'lucide-react';

export const Route = createFileRoute('/profile')({
  component: Profile,
});

function Profile() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const tgUser = getTgUser();
  const { data: backendProfile, isPending: profilePending } = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.profile(),
    placeholderData: keepPreviousData,
  });
  // Баланс приходит конвертированным в display-валюту (по умолчанию RUB) бэком —
  // делим minor units на 100, чтобы получить целые единицы для UI.
  // referral_link строится бэком из username бота, через который юзер залогинился.
  const profile = backendProfile
    ? {
        tgId: backendProfile.tg_user_id,
        username: backendProfile.tg_username,
        firstName: tgUser?.first_name ?? '',
        lastName: tgUser?.last_name ?? null,
        photoUrl: tgUser?.photo_url ?? null,
        language: (backendProfile.locale === 'en' ? 'en' : 'ru') as 'ru' | 'en',
        balanceDisplay: Math.floor(backendProfile.balance_display_minor / 100),
        displayCurrency: backendProfile.display_currency,
        referralCount: backendProfile.referrals_invited,
        referralLink: backendProfile.referral_link ?? '',
      }
    : null;
  const currencySymbol = profile?.displayCurrency === 'USD' ? '$' : '₽';
  // Реф-программа выключена партнёром → плитку не делаем кликабельной (на /me блока
  // рефералки нет, вести туда некуда). При включённой — тап ведёт к блоку на /me.
  const referralEnabled = backendProfile?.referral_enabled ?? false;

  const { sub } = useSelectedSubscription();
  const { data: loyalty } = useQuery({
    queryKey: ['loyalty'],
    queryFn: () => api.loyalty(),
    placeholderData: keepPreviousData,
  });
  const { data: info } = useQuery({
    queryKey: ['client-info'],
    queryFn: () => api.info(),
    placeholderData: keepPreviousData,
  });
  const customLinks = [
    ...(info?.custom_buttons?.menu ?? []),
    ...(info?.custom_buttons?.profile ?? []),
  ];
  // «🏪 Свой VPN» (заявка #96): создание живёт в чате бота, диплинк ведёт туда же,
  // куда и кнопка меню. Строка видна только при включённом у бота тумблере.
  const openMirror = () => {
    const tg = getWebApp();
    if (!info?.bot_username) return;
    const url = `https://t.me/${info.bot_username}?start=open_mirror`;
    if (tg?.openTelegramLink) tg.openTelegramLink(url);
    else window.open(url, '_blank');
    tg?.close();
  };
  const openSupport = () => {
    const tg = getWebApp();
    const url =
      info?.support_url ||
      (info?.bot_username ? `https://t.me/${info.bot_username}?start=open_support` : null);
    if (!url) {
      toast.error(t('Support not set up'));
      return;
    }
    if (tg?.openTelegramLink && url.startsWith('https://t.me/')) tg.openTelegramLink(url);
    else if (tg?.openLink) tg.openLink(url);
    else window.open(url, '_blank');
  };

  useEffect(() => {
    const tg = getWebApp();
    tg?.BackButton.hide();
    tg?.MainButton.hide();
  }, []);

  const displayName = tgUser
    ? [tgUser.first_name, tgUser.last_name].filter(Boolean).join(' ')
    : profile
      ? [profile.firstName, profile.lastName].filter(Boolean).join(' ')
      : t('Tg user fallback');

  const username = tgUser?.username ?? profile?.username ?? null;
  const photoUrl = tgUser?.photo_url ?? profile?.photoUrl ?? null;
  // Первые буквы слов, а не первые символы: `p[0]` у имени «🦊 Лиса» брал половину
  // эмодзи, и в кружке рисовался битый знак. Слова без букв пропускаем.
  const initials =
    displayName
      .split(/\s+/)
      .map((w) => w.match(/\p{L}/u)?.[0] ?? '')
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?';

  /** Отправить ссылку другу: выбор чата открывается сразу, без «скопируйте и вставьте».
   *  Копирование остаётся рядом — оно нужно, когда делятся не в Telegram. */
  const shareReferral = () => {
    if (!profile?.referralLink) {
      toast.error(t('Clipboard unavailable'));
      return;
    }
    haptic('light');
    getWebApp()?.openTelegramLink(
      telegramShareUrl(profile.referralLink, t('Referral share text')),
    );
  };

  const copyReferral = async () => {
    if (!profile?.referralLink) {
      toast.error(t('Clipboard unavailable'));
      return;
    }
    // Telegram WebView часто блокирует navigator.clipboard.writeText (нужен user gesture
    // + secure context + permission). Если упало — пробуем фоллбэк через скрытый textarea
    // + document.execCommand('copy'). Если и это не сработало (Telegram iOS WebView) —
    // открываем шаринг-диалог TG, чтобы юзер мог хотя бы вручную скопировать.
    const text = profile.referralLink;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        haptic('success');
        toast.success(t('Referral link copied'));
        return;
      }
      throw new Error('no clipboard');
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (ok) {
          haptic('success');
          toast.success(t('Referral link copied'));
          return;
        }
      } catch {
        /* fall through */
      }
      getWebApp()?.openTelegramLink(
        `https://t.me/share/url?url=${encodeURIComponent(text)}`,
      );
    }
  };

  if (profilePending && !backendProfile) return <ProfileSkeleton />;

  const lastAchievement = loyalty && loyalty.achieved.length > 0
    ? loyalty.achieved[loyalty.achieved.length - 1]
    : null;

  return (
    <div className="p-4 pb-24 space-y-6">
      {/* Карточка юзера */}
      <Card className="calm-card-hero overflow-hidden relative">
        <div className="absolute top-0 right-0 w-48 h-48 rounded-full bg-primary blur-[100px] opacity-[0.14] -mr-24 -mt-24 pointer-events-none" />
        <CardContent className="p-6 relative z-10 flex items-center gap-4">
          {photoUrl ? (
            <img src={photoUrl} alt="" className="w-[58px] h-[58px] rounded-2xl object-cover shrink-0" />
          ) : (
            <div
              className="w-[58px] h-[58px] rounded-2xl text-white text-xl font-bold flex items-center justify-center shrink-0"
              style={{ background: 'var(--calm-cta)' }}
            >
              {initials}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="font-extrabold text-base truncate text-foreground">{displayName}</div>
            {username && <div className="text-sm text-[#8a8c93] truncate">@{username}</div>}
            {sub?.status === 'active' && (
              <StatusPill status="active" label={`${t('Active')} · ${sub.planName}`} className="mt-2" />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Реф-плитка — только если программа включена партнёром (иначе не показываем
          нигде). При выключенной баланс занимает всю ширину. */}
      <div className={referralEnabled ? 'grid grid-cols-2 gap-3' : ''}>
        <StatCard
          icon={Wallet}
          label={t('Profile balance')}
          value={
            profile ? `${formatNumber(profile.balanceDisplay, i18n.language)}\u00a0${currencySymbol}` : '—'
          }
          onClick={() => navigate({ to: '/topup' })}
        />
        {referralEnabled && (
          <StatCard
            icon={Users}
            label={t('Profile referrals')}
            value={profile ? formatNumber(profile.referralCount, i18n.language) : '—'}
            hint={profile ? t('Profile referrals invited') : undefined}
            onClick={() => navigate({ to: '/me', state: { scrollTo: 'referral' } })}
          />
        )}
      </div>

      {/* Пополнение прямо под балансом (идея #7): раньше кнопка пряталась строкой в
          списке ниже, и её искали. */}
      <button
        type="button"
        onClick={() => navigate({ to: '/topup' })}
        className="w-full calm-cta py-3.5 rounded-2xl text-[15px] font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
      >
        <Plus className="w-5 h-5" />
        {t('Top up balance')}
      </button>

      {loyalty && (loyalty.achieved.length > 0 || loyalty.next) && (
        <Section title={t('Loyalty')}>
          <div className="p-4">
            <div className="flex items-center gap-3">
              <IconChip icon={Trophy} tone="amber" size={40} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-foreground">
                  {lastAchievement ? lastAchievement.title : t('No status')}
                </div>
                <div className="text-[11px] text-[#8a8c93]">
                  {t('Renewals count', { count: loyalty.total_renewals })}
                  {loyalty.next && loyalty.renewals_to_next !== null && (
                    <>
                      {' · '}
                      {t('Loyalty next', {
                        title: loyalty.next.title,
                        count: loyalty.renewals_to_next,
                        bonus: loyalty.next.bonus_basis_points / 100,
                      })}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* Вся лестница (идея #5): сколько даёт каждый уровень и сколько до него. */}
          {(loyalty.levels ?? []).map((lvl) => {
            const achieved = lvl.renewal_count <= loyalty.total_renewals;
            const isNext = loyalty.next?.renewal_count === lvl.renewal_count;
            return (
              <div
                key={lvl.renewal_count}
                className={cn(
                  'px-4 py-3 flex items-center gap-3',
                  isNext && 'bg-white/[0.03]',
                  !achieved && !isNext && 'opacity-60',
                )}
              >
                <span className="w-9 h-9 rounded-xl bg-white/[0.06] flex items-center justify-center text-lg shrink-0">
                  {lvl.emoji}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold leading-snug text-foreground line-clamp-2">{lvl.title}</div>
                  <div className="text-[11px] text-[#8a8c93]">
                    {t('Loyalty level bonus', { bonus: lvl.bonus_basis_points / 100 })}
                    {' · '}
                    {t('Loyalty level renewals', { count: lvl.renewal_count })}
                  </div>
                </div>
                {achieved ? (
                  <Check className="w-4 h-4 text-accent shrink-0" />
                ) : isNext && loyalty.renewals_to_next !== null ? (
                  <span className="text-[11px] text-accent shrink-0">
                    {t('Loyalty level left', { count: loyalty.renewals_to_next })}
                  </span>
                ) : null}
              </div>
            );
          })}
        </Section>
      )}

      <Section title={t('Profile section program')}>
        {profile?.referralLink ? (
          <div className="w-full p-4 flex items-center justify-between gap-2">
            {/* Основное действие — отправить: путь до друга в два тапа, как в боте.
                Копирование отдельной кнопкой, для тех, кто делится вне Telegram. */}
            <button
              type="button"
              onClick={shareReferral}
              className="flex-1 min-w-0 flex items-center text-left hover:opacity-80 transition-opacity"
            >
              <RowLeft icon={Users} title={t('Invite a Friend')} subtitle={profile.referralLink} />
            </button>
            <button
              type="button"
              onClick={copyReferral}
              aria-label={t('Copy referral link')}
              className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center hover:bg-white/[0.04] transition-colors"
            >
              <Copy className="w-4 h-4 text-accent" />
            </button>
          </div>
        ) : null}
        <WithdrawRow />
        <RowLink to="/gift" icon={Gift} title={t('Profile gift')} />
      </Section>

      <Section title={t('Profile section account')}>
        <RowLink to="/me" icon={ShieldCheck} title={t('Profile subscription')} />
        <RowLink to="/devices" icon={Smartphone} title={t('Profile devices')} />
        {/* Заморозка живёт шитом на главной — просим её сразу открыть, иначе тап по
            этой строке выглядел как «ничего не произошло». */}
        <RowLink to="/me" icon={Snowflake} title={t('Profile freeze')} state={{ openSheet: 'freeze' }} />
        <RowLink to="/payments" icon={HistoryIcon} title={t('Payments history')} />
      </Section>

      {/* Кнопки партнёра из редактора «Тексты → Кнопки» (идея #10): те же, что в
          боте. Ссылка уходит в Telegram, экран мини-аппа открывается на месте. */}
      {customLinks.length > 0 && (
        <Section title={t('Links')}>
          {customLinks.map((b, i) => (
            <button
              key={`${b.text}-${i}`}
              type="button"
              onClick={() => {
                haptic('light');
                if (b.path) navigate({ to: b.path as '/' });
                else if (b.url) openInvoiceUrl(b.url);
              }}
              className="w-full p-4 flex items-center justify-between hover:bg-white/[0.02] transition-colors text-left"
            >
              <RowLeft icon={b.path ? ChevronRight : ExternalLink} title={b.text} />
              <ChevronRight className="w-4 h-4 text-[#8a8c93]" />
            </button>
          ))}
        </Section>
      )}

      <Section title={t('Profile section help')}>
        <button
          type="button"
          onClick={openSupport}
          className="w-full p-4 flex items-center justify-between hover:bg-white/[0.02] transition-colors text-left"
        >
          <RowLeft icon={MessageSquare} title={t('Support')} />
          <ChevronRight className="w-4 h-4 text-[#8a8c93]" />
        </button>
        <RowLink to="/faq" icon={HelpCircle} title="FAQ" />
        {api.getAuth().mirrorsEnabled && info?.bot_username && (
          <button
            type="button"
            onClick={openMirror}
            className="w-full p-4 flex items-center justify-between hover:bg-white/[0.02] transition-colors text-left"
          >
            <RowLeft icon={Store} title={t('Own VPN')} subtitle={t('Own VPN hint')} />
            <ChevronRight className="w-4 h-4 text-[#8a8c93]" />
          </button>
        )}
        {backendProfile?.site_login_available && <SiteRow />}
        {/* Раздел отключаем, если партнёр убрал его у своего бота (флаг из auth-ответа). */}
        {api.getAuth().statusPageEnabled && (
          <RowLink to="/status" icon={Activity} title={t('Network status')} />
        )}
        <RowLink to="/settings" icon={SettingsIcon} title={t('Settings')} />
        <BiometricRow />
        <button
          type="button"
          onClick={() => {
            // Оба хранилища: bot_id живёт в sessionStorage, цвет и прочее в localStorage.
            window.localStorage.clear();
            window.sessionStorage.clear();
            toast.success(t('Local cache cleared'));
          }}
          className="w-full p-4 flex items-center justify-between hover:bg-white/[0.02] transition-colors text-left"
        >
          <RowLeft icon={LogOut} tone="red" danger title={t('Profile logout')} />
          <ChevronRight className="w-4 h-4 text-[#8a8c93]" />
        </button>
      </Section>

      <p className="text-center text-xs pt-4" style={{ fontFamily: 'var(--font-mono)', color: '#5a5c63' }}>
        {t('Profile version')}: {APP_VERSION} · tg id {tgUser?.id ?? profile?.tgId ?? '—'}
      </p>
    </div>
  );
}

/**
 * Заголовки секций настоящие: их состав от данных не зависит, ждать нечего.
 * Плейсхолдерами кроем только карточку человека, баланс с рефералами и строки
 * разделов — раньше вместо них стояли три глухие плиты в 140-280px, и экран
 * при появлении данных перекладывался целиком.
 */
function ProfileSkeleton() {
  const { t } = useTranslation();
  const sections: [string, number][] = [
    [t('Profile section program'), 2],
    [t('Profile section account'), 4],
    [t('Profile section help'), 6],
  ];
  return (
    <SkeletonPage className="space-y-6">
      <SkeletonCard className="rounded-[24px] p-6 flex items-center gap-4">
        <Skeleton className="w-[58px] h-[58px] rounded-2xl shrink-0" />
        <div className="min-w-0 flex-1 space-y-2">
          <SkeletonLine size="lg" className="w-2/5" />
          <SkeletonLine size="sm" className="w-1/3" />
          <Skeleton className="h-[22px] w-36 rounded-full" />
        </div>
      </SkeletonCard>

      <div className="grid grid-cols-2 gap-3">
        {[0, 1].map((i) => (
          <SkeletonCard key={i} className="rounded-[24px] p-4 flex flex-col gap-3">
            <Skeleton className="w-9 h-9 rounded-[10px]" />
            <div className="space-y-1.5">
              <SkeletonLine size="xs" className="w-16" />
              <SkeletonLine size="title" className="w-20" />
            </div>
          </SkeletonCard>
        ))}
      </div>

      {/* Кнопка пополнения под балансом: без плашки страница подпрыгивала бы на её
          высоту при появлении данных. */}
      <Skeleton className="h-[52px] w-full rounded-2xl" />

      {sections.map(([title, rows]) => (
        <div key={title} className="space-y-2">
          <SectionLabel className="px-2">{title}</SectionLabel>
          <SkeletonCard className="rounded-[24px] min-h-0 divide-y divide-white/5">
            {Array.from({ length: rows }, (_, i) => (
              <div key={i} className="flex gap-4 items-center p-4">
                <Skeleton className="w-9 h-9 rounded-[10px] shrink-0" />
                <SkeletonLine className="w-1/3" />
              </div>
            ))}
          </SkeletonCard>
        </div>
      ))}
    </SkeletonPage>
  );
}

function StatCard({
  icon,
  label,
  value,
  hint,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  /** Подпись под числом: само число крупно, слово мелко, иначе оно рвалось посреди. */
  hint?: string;
  onClick?: () => void;
}) {
  return (
    <Card
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={
        onClick
          ? () => {
              haptic('light');
              onClick();
            }
          : undefined
      }
      className={cn(onClick && 'cursor-pointer active:scale-[0.98] transition-transform')}
    >
      <CardContent className="p-4 flex flex-col gap-3">
        <IconChip icon={icon} tone="blue" size={36} radius={10} />
        <div>
          <SectionLabel className="mb-0.5">{label}</SectionLabel>
          <div className="text-xl font-bold leading-tight tabular text-foreground break-words">{value}</div>
          {hint && <div className="text-[11px] text-[#8a8c93] mt-0.5">{hint}</div>}
        </div>
      </CardContent>
    </Card>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <SectionLabel className="px-2">{title}</SectionLabel>
      <Card className="overflow-hidden">
        <CardContent className="p-0 divide-y divide-white/5">{children}</CardContent>
      </Card>
    </div>
  );
}

/**
 * Тумблер подтверждения покупок биометрией.
 *
 * До этого Face ID/отпечаток запрашивался перед каждой оплатой у всех и отключить его
 * было нельзя. Выключение НЕ снимает подтверждение вовсе: остаётся обычный попап
 * «Оплатить N ₽?», который и защищает от случайного двойного тапа по кнопке оплаты.
 */
function BiometricRow() {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(
    () => api.getAuth().biometricConfirmEnabled !== false,
  );
  const [saving, setSaving] = useState(false);

  const toggle = async (next: boolean) => {
    if (saving) return;
    setSaving(true);
    setEnabled(next);
    try {
      await api.setBiometricConfirm(next);
      haptic('success');
    } catch {
      setEnabled(!next); // откат: иначе тумблер врёт про сохранённое состояние
      haptic('error');
      toast.error(t('Setting save failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full p-4 flex items-center justify-between gap-3">
      <RowLeft
        icon={Fingerprint}
        title={t('Biometry row')}
        subtitle={t('Biometry row desc')}
      />
      <Switch
        checked={enabled}
        disabled={saving}
        aria-label={t('Biometry row')}
        onCheckedChange={(v) => void toggle(v)}
      />
    </div>
  );
}

/**
 * Вывод реферального заработка (идея #162). Строка нужна и здесь, а не только в
 * карточке рефералки на главной: без живой подписки главная показывает выбор тарифа,
 * и до заработанного было бы не добраться. До ответа строки нет: это переход, а не
 * цифра, ложного «0 ₽» она не покажет.
 */
function WithdrawRow() {
  const { t, i18n } = useTranslation();
  const { data } = useQuery({
    queryKey: WITHDRAW_QUERY_KEY,
    queryFn: () => api.referralWithdrawState(),
    staleTime: 30_000,
  });
  if (!data || (!data.enabled && !data.open)) return null;
  const money = (minor: number) => formatWholeMinor(minor, data.currency, i18n.language);
  const subtitle = !data.open
    ? t('Withdraw available short', { amount: money(data.available_display_minor) })
    : data.open.status === 'processing'
      ? t('Withdraw processing short', { amount: money(data.open.amount_display_minor) })
      : t('Withdraw open short', { amount: money(data.open.amount_display_minor) });
  return (
    <Link
      to="/referral-withdraw"
      className="w-full p-4 flex items-center justify-between hover:bg-white/[0.02] transition-colors"
    >
      <RowLeft icon={Wallet} title={t('Withdraw title')} subtitle={subtitle} />
      <ChevronRight className="w-4 h-4 text-[#8a8c93]" />
    </Link>
  );
}

/**
 * «Открыть в браузере» (заявка #142): кабинет на сайте-витрине без пароля. Строка
 * есть, только когда у бота работает витрина (`site_login_available` с сервера).
 */
function SiteRow() {
  const { t } = useTranslation();
  const { open, pending } = useOpenSite();
  return (
    <button
      type="button"
      onClick={open}
      disabled={pending}
      className="w-full p-4 flex items-center justify-between hover:bg-white/[0.02] transition-colors text-left disabled:opacity-70"
    >
      <RowLeft icon={Laptop} title={t('Open in browser')} subtitle={t('Open in browser hint')} />
      {pending ? (
        <Loader2 className="w-4 h-4 text-[#8a8c93] animate-spin" />
      ) : (
        <ExternalLink className="w-4 h-4 text-[#8a8c93]" />
      )}
    </button>
  );
}

function RowLink({
  to,
  icon,
  title,
  state,
}: {
  to: string;
  icon: LucideIcon;
  title: string;
  /** Router-state для целевой страницы (например «открой шит заморозки»). */
  state?: Record<string, unknown>;
}) {
  return (
    <Link
      to={to}
      state={state}
      className="w-full p-4 flex items-center justify-between hover:bg-white/[0.02] transition-colors"
    >
      <RowLeft icon={icon} title={title} />
      <ChevronRight className="w-4 h-4 text-[#8a8c93]" />
    </Link>
  );
}

function RowLeft({
  icon,
  title,
  subtitle,
  tone = 'blue',
  danger,
}: {
  icon: LucideIcon;
  title: string;
  subtitle?: string;
  tone?: Tone;
  danger?: boolean;
}) {
  return (
    <div className="flex gap-4 items-center min-w-0">
      <IconChip icon={icon} tone={tone} size={36} radius={10} />
      <div className="min-w-0">
        {/* Две строки вместо одной: подписи кнопок партнёра и «Подтверждать отпечатком»
            обрывались на полуслове уже на 375px. */}
        <div className={cn('font-semibold leading-snug line-clamp-2', danger ? 'text-[#f0656a]' : 'text-foreground')}>{title}</div>
        {subtitle && <div className="text-xs text-[#8a8c93] line-clamp-2 [overflow-wrap:anywhere]">{subtitle}</div>}
      </div>
    </div>
  );
}
